/* eslint-disable react/prop-types */
import React, {
  useEffect, useState, useCallback,
} from 'react';
import * as PropTypes from 'prop-types';
import {
  Form, Row, Typography, Skeleton, Button, Upload, Input, PageHeader, Icon, Col, Result, Select, Spin,
} from 'antd';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import '../Configure.css';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserGroup, requestPageData } from '../../../../state/ui/configuration/company/companyUiActions';
import { selectUpdateGroupRequest, selectCompanyPageDataRequest } from '../../../../state/ui/configuration/company/companyUiReducer';
import { selectUserGroup } from '../../../../state/domain/company/companyReducer';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { userIsAdmin } from './user-util';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import './ContactDetails.css';
import { selectLanguages, selectCurrencies } from '../../../../state/domain/appConfig/appConfigReducer';
import routeLocations from '../../../../utility/route-locations';
import AddressInput from '../../../../components/fields/AddressInput';

const { Text } = Typography;


const CompanyNameField = injectIntl(({ form, userGroup, intl }) => (
  <Form.Item required label={<FormattedMessage id="companyName" />}>
    { form.getFieldDecorator('description', {
      initialValue: userGroup ? userGroup.description : null,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear={!!userGroup}
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

const TaxReferenceField = injectIntl(({ form, userGroup, intl }) => (
  <Form.Item label={<FormattedMessage id="taxRefNo" />}>
    { form.getFieldDecorator('taxReferenceNo', {
      initialValue: userGroup ? userGroup.taxReferenceNo : null,
    })(
      <Input
        type="text"
        maxLength={50}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

const CompanyLanguageField = ({ form, userGroup, languages }) => (
  <Form.Item required label={<FormattedMessage id="preferredLanguage" />}>
    { form.getFieldDecorator('defaultLocale', {
      initialValue: userGroup && userGroup.defaultLocale,
    })(
      <Select
        size="default"
        className="my-details-locale"
      >
        { languages ? languages.map(language => (
          <Select.Option key={language.languageCode} value={language.languageCode}>
            <Row type="flex" align="middle">
              <ReactCountryFlag code={language.languageCode.slice(language.languageCode.indexOf('_') + 1, language.languageCode.length).toLowerCase()} svg />
              <Typography.Text>
                &nbsp;
                {language.languageDescription}
              </Typography.Text>
            </Row>
          </Select.Option>
        )) : <Select.Option key="spin" value="spin"><Spin /></Select.Option>}
      </Select>,
    )}
  </Form.Item>
);

CompanyLanguageField.propTypes = {
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  userGroup: PropTypes.shape({
    description: PropTypes.string.isRequired,
    defaultLocale: PropTypes.string.isRequired,
    address: PropTypes.shape({
      line1: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
    }),
    logo: PropTypes.string,
  }).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape({
    languageCode: PropTypes.string.isRequired,
    languageDescription: PropTypes.string.isRequired,
  })),
};

CompanyLanguageField.defaultProps = {
  languages: null,
};

const ContactEmailField = injectIntl(({ form, userGroup, intl }) => (
  <Form.Item required label={<FormattedMessage id="contactByEmail" />}>
    {
      form.getFieldDecorator('contactEmail', {
        initialValue: userGroup ? userGroup.contactEmail : null,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
          { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
        ],
      })(
          <Input
              type="email"
              maxLength={80}
              allowClear
              placeholder={intl.formatMessage({ id: 'enter' })}
          />,
      )
    }
  </Form.Item>
));

ContactEmailField.propTypes = {
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  userGroup: PropTypes.shape({
    contactEmail: PropTypes.string,
  }),
};

ContactEmailField.defaultProps = {
  userGroup: null,
};

const ContactPhoneField = injectIntl(({ form, userGroup, intl }) => (
    <Form.Item label={<FormattedMessage id="contactByPhone" />}>
      {
        form.getFieldDecorator('contactPhone', {
          initialValue: userGroup ? userGroup.contactPhone : null,
        })(
            <Input
                type="text"
                maxLength={80}
                allowClear
                placeholder={intl.formatMessage({ id: 'enter' })}
            />,
        )
      }
    </Form.Item>
));

ContactPhoneField.propTypes = {
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  userGroup: PropTypes.shape({
    contactPhone: PropTypes.string,
  }),
};

ContactPhoneField.defaultProps = {
  userGroup: null,
};

const CurrencyField = ({ form, userGroup, currencies }) => (
  <Form.Item required label={<FormattedMessage id="preferredCurrency" />}>
    {
      form.getFieldDecorator('currency', {
        initialValue: userGroup && userGroup.currency && userGroup.currency.currencyCode,
      })(
        <Select
          showSearch
          filterOption={(input, option) => {
            if (option.props.code && option.props.code === input) return true;
            if (option.props.description && option.props.description.toLowerCase().includes(input.toLowerCase())) return true;
            return false;
          }}
          loading={!currencies}
          className="currency-select"
        >
          {
            currencies
              ? [...currencies].sort((a, b) => a.currencyCode.localeCompare(b.currencyCode)).map(currency => (
                <Select.Option description={currency.displayCode} code={currency.currencyCode} key={currency.currencyCode}>{`${currency.currencyCode} - ${currency.displayCode}`}</Select.Option>))
              : <Select.Option disabled key="-1" />
          }
        </Select>,
      )
    }
  </Form.Item>
);

CurrencyField.propTypes = {
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  userGroup: PropTypes.shape({
    currency: PropTypes.shape({
      currencyCode: PropTypes.string,
    }),
  }),
  currencies: PropTypes.arrayOf(PropTypes.shape({
    currencyCode: PropTypes.string,
    displayCode: PropTypes.string,
  })),
};

CurrencyField.defaultProps = {
  currencies: null,
  userGroup: null,
};


// Creates an address form item, decorator is address, returns an object matching address.
function AddressField({
  initialValue,
  className,
  label,
  form,
  required,
  countries,
  getCountries,
  ...props
}) {
  const intl = useIntl();
  const [validationStatus, setValidationStatus] = useState({});
  return (
    <Form.Item label={label} className={className} validateStatus="success" {...props}>
      { form.getFieldDecorator('address', {
        initialValue: initialValue || {},
        rules: [
          {
            validator(rule, value, callback) {
              let newValidationStatus = {};
              if (!value.country) {
                callback(true);
                newValidationStatus = { ...newValidationStatus, country: intl.formatMessage({ id: 'addCountryRequired' }) };
              }

              if (!value.line1) {
                callback(true);
                newValidationStatus = { ...newValidationStatus, line1: intl.formatMessage({ id: 'addLine1Required' }) };
              }

              setValidationStatus(newValidationStatus);
              callback();
            },
          },
        ],
      })(
        <AddressInput validationStatus={validationStatus} />,
      )}
    </Form.Item>
  );
}

/**
 * The page for editing contact details of a company.
 * @param {object} props The react props.
 */
function EditContactDetails({
  form, history, wizard, wizardNext,
}) {
  const intl = useIntl();

  // React State
  const [fileList, setFileList] = useState([]); // A list of the icons. Only has at most 1 item. (The company logo)

  // Redux State
  const pageDataRequest = useSelector(selectCompanyPageDataRequest);
  const userGroup = useSelector(selectUserGroup);
  const sessionUser = useSelector(selectSessionUser);
  const languages = useSelector(selectLanguages);
  const currencies = useSelector(selectCurrencies);
  const updateRequest = useSelector(selectUpdateGroupRequest);
  const updateError = updateRequest ? updateRequest.error : null;
  const updateLoading = updateRequest ? updateRequest.isLoading : false;

  // Redux Actions
  const dispatch = useDispatch();
  const loadPageData = useCallback(() => dispatch(requestPageData()), [dispatch]);

  // Load the initial page data.
  useEffect(() => {
    if (!pageDataRequest) loadPageData();
  }, [pageDataRequest, loadPageData]);

  // Update the icon 'list' to have the
  useEffect(() => {
    if (!userGroup || !userGroup.logoType) return;

    setFileList([{
      uid: userGroup.description,
      name: userGroup.description,
      status: 'done',
      contentType: userGroup.logoType,
      url: `data:${userGroup.logoType};base64,${userGroup.logo}`,
    }]);
  }, [userGroup, setFileList, intl]);

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the form from submitting traditionally.

    try {
      const values = await form.validateFields();
      const newValues = {
        address: values.address,
        defaultLocale: values.defaultLocale,
        description: values.description,
        logoType: values.logoType,
        logo: values.logo,
        contactEmail: values.contactEmail,
        contactPhone: values.contactPhone,
        currencyCode: values.currency,
        taxReferenceNo: values.taxReferenceNo,
      };
      dispatch(updateUserGroup(newValues, history, wizard, wizardNext));
    } catch (_) {
      // Validation has failed. Nothing for us to do.
    }
  };

  // Admin users must not be able to modify the company data.
  if (!userIsAdmin(sessionUser)) {
    return (
      <Result
        status="403"
        title={<FormattedMessage id="accessRestricted" />}
        subTitle={<FormattedMessage id="insufficientPermissions" />}
      />
    );
  }

  return (
    <div>
      <PageHeader
        onBack={() => history.push(routeLocations.configure_contact_details)}
        style={{ display: wizard ? 'none' : '', width: '100%' }}
        title={(<Text><FormattedMessage id="contactDetails" /></Text>)}
      />
      <div style={{ padding: wizard ? '0' : '0 1em' }}>
        <ViaValidationAlert validationError={updateError} style={{ marginLeft: '60px', marginRight: '30px' }} />
        <Form onSubmit={e => handleSubmit(e)}>
          <Row>
            {userGroup ? <CompanyNameField form={form} userGroup={userGroup} /> : <Skeleton title={false} paragraph={{ width: [200], rows: 1 }} />}
          </Row>
          <Row>
            {userGroup ? <TaxReferenceField form={form} userGroup={userGroup} /> : <Skeleton title={false} paragraph={{ width: [200], rows: 1 }} />}
          </Row>
          <Row>
            <Form.Item label={<FormattedMessage id="companyLogo" />}>
              { form.getFieldDecorator('logo', {
                initialValue: userGroup && userGroup.logo,
                rules: [
                  {
                    validator(rule, value, callback) {
                      const logoType = form.getFieldValue('logoType');
                      if (logoType && !['image/jpg', 'image/jpeg', 'image/png'].find((type) => type === logoType)) {
                        callback(intl.formatMessage({ id: 'fileMustHaveAnAcceptedContentType' }, { 0: '[image/jpg, image/jpeg, image/png]' }));
                      }
                      callback();
                    },
                  },
                ],
              })(
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Upload
                    className="avatar-uploader"
                    accept=".png,.jpg,.jpeg"
                    showUploadList={false}
                    listType="text"
                    fileList={fileList}
                    beforeUpload={(file) => {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        const dataUrl = e.target.result;
                        // sets the logo data and datatype
                        form.setFieldsValue({ logo: dataUrl.slice(dataUrl.indexOf(',') + 1), logoType: dataUrl.slice(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';')) });
                        form.validateFields(['logo']);
                      };
                      // reads the data of the file into a string of style "data:{contentType};base64,{fileDataInBase64}"
                      reader.readAsDataURL(file);
                      return false; // Prevent upload
                    }}
                    onChange={(info) => { setFileList(info.fileList.slice(-1)); }} // Updates the file list
                    onRemove={() => form.setFieldsValue({ logo: null, logoType: null })} // removes the logo
                  >
                    {fileList.length !== 0
                      ? (
                        <img src={`data:${form.getFieldValue('logoType')};base64,${form.getFieldValue('logo')}`} alt="" className="via-fake-upload" style={{ cursor: 'pointer' }} />
                      )
                      : (
                        <Row className="via-fake-upload" style={{ padding: '5px', cursor: 'pointer' }}>
                          <Col span={24}>
                            <Icon type={updateLoading ? 'loading' : 'plus'} style={{ fontSize: '30px', marginTop: '5px' }} />
                          </Col>
                          <Col span={24}>
                            <Text>{intl.formatMessage({ id: 'upload' })}</Text>
                          </Col>
                        </Row>
                      )}
                  </Upload>
                  {fileList.length !== 0
                    ? <Button style={{ marginTop: '0.3em', width: '120px' }} onClick={() => { form.setFieldsValue({ logo: null, logoType: null }); setFileList([]); }}><FormattedMessage id="remove" /></Button>
                    : <></>}
                </div>,
              )}
            </Form.Item>
            <Form.Item style={{ height: '0', marginBottom: '0', marginTop: '0' }}>
              {
                // antd refuses to let me add arbitrary form fields without a relevant render.
                // this creates a dud field for the logo type since in reality it is part of the upload field above
                form.getFieldDecorator('logoType', { initialValue: userGroup && userGroup.logoType, rules: [] })(<div />)
              }
            </Form.Item>
          </Row>
          <Row>
            <Row>
              {userGroup ? <AddressField form={form} initialValue={userGroup.address} required label={<FormattedMessage id="companyAddress" />} /> : <Skeleton title={false} paragraph={{ width: [200, 200, 200, 200, 100, 100, 100], rows: 7 }} />}
            </Row>
          </Row>
          <Row>
            <ContactEmailField form={form} userGroup={userGroup} />
          </Row>
          <Row>
            <ContactPhoneField form={form} userGroup={userGroup} />
          </Row>
          <Row>
            <CurrencyField form={form} userGroup={userGroup} currencies={currencies} />
          </Row>
          <Row>
            {userGroup
              ? <CompanyLanguageField form={form} userGroup={userGroup} languages={languages} />
              : <Skeleton title={false} paragraph={{ width: 200, rows: 1 }} />}
          </Row>
          <Form.Item>
            <Row type="flex" justify="end" style={{ marginTop: '0.5em', marginRight: '2em' }}>
              <Button type="link" style={{ display: wizard ? '' : 'none', marginRight: '0.5em' }} onClick={() => wizardNext(true)}>
                <FormattedMessage id="configureLater" />
              </Button>
              <Button type="primary" htmlType="submit"><FormattedMessage id="save" /></Button>
            </Row>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}

EditContactDetails.propTypes = {
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  wizard: PropTypes.bool,
  wizardNext: PropTypes.func,
};

EditContactDetails.defaultProps = {
  wizard: false,
  wizardNext: () => {},
};


export default withRouter(Form.create({ name: 'edit_contact_details' })(EditContactDetails));
